import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    ModalHeader,
} from "../Styles/InputStyles";
import { MdClose, MdEdit } from "react-icons/md";
import tw from "tailwind-styled-components";
import { beneficiary_type_roles } from "../../Config";
import SelectOptionModal from "../SelectOptionModal";
import VerifyBankAccount from "../Common/VerfiyBankAccount";

const AddBeneficiary = ({
    addBaneficiaryAccountMutate,
    addBaneficiaryAccountLoading,
    handleClose,
    verifyBankAccount,
    verifyAccountLoading,
    isAccountVerified,
    verifiedAccountName,
    setVerifiedAccountName,
    verifyErrorMsg,
    clearVerification
}) => {
    const initialValues = {
        name: "",
        mobileNo: "",
        accountName: "",
        accountNumber: "",
        ifsc: "",
        beneficiaryType: "",
    };

    const [height, setHeight] = useState(window.innerHeight);
    const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const beneficiaryRoleOptions = beneficiary_type_roles.map((role) => ({
        label: role.label,
        value: role.value,
    }));

    // Yup Validation Schema
    const validationSchema = Yup.object({
        name: Yup.string()
            .required("Beneficiary name is required")
            .matches(
                /^[A-Za-z0-9. ]+$/,
                "Beneficiary name can only contain letters, numbers, spaces & dots"
            )
            .min(4, "Beneficiary name must be at least 4 characters long")
            .max(100, "Beneficiary name cannot exceed 100 characters"),

        mobileNo: Yup.string()
            .required("Mobile number is required")
            .matches(/^[0-9]+$/, "Mobile number must contain only numbers")
            .length(10, "Mobile number must be exactly 10 digits"),

        accountName: Yup.string()
            .required("Account name is required")
            .matches(
                /^[A-Za-z0-9. ]+$/,
                "Account name can only contain letters, numbers, spaces & dots"
            )
            .min(3, "Account name must be at least 3 characters long")
            .max(100, "Account name cannot exceed 100 characters"),

        accountNumber: Yup.string()
            .required("Account number is required")
            .matches(/^[0-9]+$/, "Account number must contain only numbers")
            .min(9, "Account number must be at least 9 digits")
            .max(18, "Account number cannot exceed 18 digits"),

        ifsc: Yup.string()
            .required("IFSC code is required")
            .matches(
                /^[A-Z]{4}0[A-Z0-9]{6}$/,
                "Enter a valid IFSC code (e.g., SBIN0012345)"
            )
            .transform(value => value.toUpperCase()),

        beneficiaryType: Yup.number()
            .required("Beneficiary type is required")
            .typeError("Beneficiary type is required"),
    });

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        const payload = {
            name: values.name,
            mobile: values.mobileNo,
            accountNumber: values.accountNumber,
            ifsc: values.ifsc,
            accountName: values.accountName,
            beneType: values.beneficiaryType,
        };

        addBaneficiaryAccountMutate(payload);
    };

    return (
        <Container
            style={{
                height: `${height}px`,
                overflow: "auto",
                WebkitOverflowScrolling: "touch",
            }}
        >
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Add Beneficiary</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>
            <div className="custom-scrollbar px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    validateOnChange={true}
                    validateOnBlur={true}
                >
                    {({ errors, touched, isSubmitting, values, setFieldValue }) => {
                        const getCurrentRoleLabel = () => {
                            const selectedRole = beneficiary_type_roles.find(
                                (role) => role.value === values?.beneficiaryType
                            );
                            return selectedRole ? selectedRole.label : "Select beneficiary type";
                        };

                        const handleRoleSelect = (selectedValue) => {
                            setFieldValue("beneficiaryType", selectedValue);
                        };

                        return (
                            <Form>
                                <ScrollableContent className="max-h-[calc(100vh-140px)]">
                                    <Wrapper>
                                        {/* Beneficiary Type Selection - Similar to Staff Role */}
                                        <InputGroup>
                                            <Label htmlFor="beneficiaryType">Beneficiary Type *</Label>
                                            <FieldWrappers onClick={() => setIsRoleModalOpen(true)}>
                                                <Field
                                                    type="text"
                                                    name="beneficiaryTypeDisplay"
                                                    id="beneficiaryTypeDisplay"
                                                    autoComplete="off"
                                                    readOnly
                                                    value={getCurrentRoleLabel()}
                                                    className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                    placeholder="Select beneficiary type"
                                                />
                                                <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                            </FieldWrappers>
                                            {errors.beneficiaryType && touched.beneficiaryType && (
                                                <ErrorText>{errors.beneficiaryType}</ErrorText>
                                            )}
                                        </InputGroup>
                                        <InputGroup>
                                            <Label htmlFor="name">Beneficiary Name *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="name"
                                                    id="name"
                                                    autoComplete="off"
                                                    placeholder="Enter beneficiary name"
                                                    className={errors.name && touched.name ? "border-red-500" : ""}
                                                />
                                            </FieldWrappers>
                                            {errors.name && touched.name && (
                                                <ErrorText>{errors.name}</ErrorText>
                                            )}
                                        </InputGroup>

                                        <InputGroup>
                                            <Label htmlFor="mobileNo">Mobile Number *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="tel"
                                                    name="mobileNo"
                                                    id="mobileNo"
                                                    autoComplete="off"
                                                    maxLength="10"
                                                    placeholder="Enter mobile number"
                                                    className={errors.mobileNo && touched.mobileNo ? "border-red-500" : ""}
                                                />
                                            </FieldWrappers>
                                            {errors.mobileNo && touched.mobileNo && (
                                                <ErrorText>{errors.mobileNo}</ErrorText>
                                            )}
                                        </InputGroup>

                                        <InputGroup>
                                            <Label htmlFor="accountName">Account Holder Name *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="accountName"
                                                    id="accountName"
                                                    autoComplete="off"
                                                    placeholder="Enter account name"
                                                    className={errors.accountName && touched.accountName ? "border-red-500" : ""}
                                                />
                                                {isAccountVerified &&
                                                    values.accountName?.trim().toLowerCase() ===
                                                    verifiedAccountName?.trim().toLowerCase() && (
                                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600 text-xs font-medium">
                                                            ✓ Verified
                                                        </span>
                                                    )}
                                            </FieldWrappers>
                                            {errors.accountName && touched.accountName && (
                                                <ErrorText>{errors.accountName}</ErrorText>
                                            )}
                                            {isAccountVerified &&
                                                values.accountName?.trim().toLowerCase() !==
                                                verifiedAccountName?.trim().toLowerCase() && (
                                                    <div className="mt-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <p className="text-sm font-medium text-yellow-800">
                                                                    Name Mismatch
                                                                </p>

                                                                <p className="text-xs text-yellow-700 mt-1">
                                                                    Bank account holder name:{" "}
                                                                    <span className="font-semibold">
                                                                        {verifiedAccountName}
                                                                    </span>
                                                                </p>
                                                            </div>

                                                            <button
                                                                type="button"
                                                                className="text-xs font-semibold text-cblue-default hover:underline"
                                                                onClick={() => {
                                                                    setFieldValue(
                                                                        "accountName",
                                                                        verifiedAccountName,
                                                                    );
                                                                    //setVerifiedAccountName ('');
                                                                }
                                                                }
                                                            >
                                                                Use Verified Name
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                        </InputGroup>

                                        <InputGroup>
                                            <Label htmlFor="accountNumber">Account Number *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="accountNumber"
                                                    id="accountNumber"
                                                    autoComplete="off"
                                                    onChange={(e) => {
                                                        setFieldValue(
                                                            "accountNumber",
                                                            e.target.value.replace(/\s/g, "")
                                                        );
                                                        clearVerification();
                                                    }}
                                                    placeholder="Enter account number"
                                                    className={errors.accountNumber && touched.accountNumber ? "border-red-500" : ""}
                                                />
                                            </FieldWrappers>
                                            {errors.accountNumber && touched.accountNumber && (
                                                <ErrorText>{errors.accountNumber}</ErrorText>
                                            )}
                                        </InputGroup>

                                        <InputGroup>
                                            <Label htmlFor="ifsc">IFSC *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="ifsc"
                                                    id="ifsc"
                                                    autoComplete="off"
                                                    className={errors.ifsc && touched.ifsc ? "border-red-500" : ""}
                                                    placeholder="Enter IFSC code"
                                                />
                                            </FieldWrappers>
                                            {errors.ifsc && touched.ifsc && (
                                                <ErrorText>{errors.ifsc}</ErrorText>
                                            )}
                                            <VerifyBankAccount
                                                loading={verifyAccountLoading}
                                                verified={isAccountVerified}
                                                error={verifyErrorMsg}
                                                disabled={!values.accountNumber || !values.ifsc}
                                                onVerify={() =>
                                                    verifyBankAccount({
                                                        accountNumber: values.accountNumber,
                                                        ifsc: values.ifsc,
                                                    })
                                                }
                                            />
                                        </InputGroup>


                                    </Wrapper>
                                </ScrollableContent>

                                <ActionSection>
                                    <SideCancelBtn type="button" onClick={handleClose}>
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn type="submit" disabled={isSubmitting || addBaneficiaryAccountLoading}>
                                        {isSubmitting || addBaneficiaryAccountLoading ? "Submitting..." : "Submit"}
                                    </SubmitBtn>
                                </ActionSection>

                                {/* Beneficiary Type Selection Modal */}
                                <SelectOptionModal
                                    isOpen={isRoleModalOpen}
                                    onClose={() => setIsRoleModalOpen(false)}
                                    title="Select Beneficiary Type"
                                    options={beneficiaryRoleOptions}
                                    selectedValue={values.beneficiaryType}
                                    onSelect={handleRoleSelect}
                                    searchPlaceholder="Search beneficiary types..."
                                />
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5 mt-1`;

export default AddBeneficiary;